/**
 * Token Manager for Supabase Auth sessions.
 * Handles automatic token refresh and session management.
 */
import store from "../store/store";

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.refreshPromise = null;
    this.minTimeBeforeRefresh = 5 * 60 * 1000; // 5 minutes in ms
    this.refreshThreshold = 0.8; // Refresh when 80% of token lifetime has passed
  }

  /**
   * Initialize token monitoring
   */
  init() {
    // Start monitoring when user logs in
    store.subscribe(mutation => {
      if (mutation.type === "auth/SET_CURRENT_USER") {
        if (mutation.payload !== null && mutation.payload.token) {
          this.startTokenMonitoring(mutation.payload);
        } else {
          this.stopTokenMonitoring();
        }
      }
    });

    // Check if user is already logged in
    const currentUser = store.getters['auth/currentUser'];
    if (currentUser && currentUser.token) {
      this.startTokenMonitoring(currentUser);
    }
  }

  /**
   * Start monitoring token expiration
   * @param {Object} user - Current user object with token
   */
  startTokenMonitoring(user) {
    this.stopTokenMonitoring(); // Clear any existing timer

    if (!user.token || !user.token.expires_at) {
      console.warn('Token monitoring: No expiration time found');
      return;
    }

    const expiresAt = user.token.expires_at;
    const now = Date.now();
    const timeUntilExpiry = expiresAt - now;
    const refreshTime = timeUntilExpiry * this.refreshThreshold;

    // Don't set a timer if token expires too soon
    if (refreshTime < this.minTimeBeforeRefresh) {
      console.warn('Token expires too soon, attempting immediate refresh');
      this.refreshToken();
      return;
    }

    console.log(`Token monitoring: Setting refresh timer for ${Math.round(refreshTime / 1000 / 60)} minutes`);

    this.refreshTimer = setTimeout(() => {
      this.refreshToken();
    }, refreshTime);
  }

  /**
   * Stop token monitoring
   */
  stopTokenMonitoring() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Check if current token is expired or expiring soon
   * @returns {boolean} True if token needs refresh
   */
  needsRefresh() {
    const currentUser = store.getters['auth/currentUser'];
    if (!currentUser || !currentUser.token || !currentUser.token.expires_at) {
      return false;
    }

    const now = Date.now();
    const expiresAt = currentUser.token.expires_at;
    const timeUntilExpiry = expiresAt - now;
    const timeElapsed = (currentUser.token.expires_in * 1000) - timeUntilExpiry;
    const tokenLifetime = currentUser.token.expires_in * 1000;

    // Need refresh if 80% of token lifetime has passed or token expires in less than 5 minutes
    return (timeElapsed / tokenLifetime) >= this.refreshThreshold || timeUntilExpiry < this.minTimeBeforeRefresh;
  }

  /**
   * Refresh the current user token
   * @returns {Promise<boolean>} Success of refresh operation
   */
  async refreshToken() {
    // If a refresh is already in progress, await it instead of failing fast.
    // This prevents concurrent API calls from throwing even though the refresh
    // completes successfully a moment later.
    if (this.isRefreshing) {
      console.log('Token refresh already in progress');
      return this.refreshPromise ? await this.refreshPromise : false;
    }

    this.isRefreshing = true;
    this.refreshPromise = (async () => {
      try {
        console.log('Attempting to refresh user token...');

        // Use the store's attemptRefresh action which has been improved
        await store.dispatch('auth/attemptRefresh');

        const refreshedUser = store.getters['auth/currentUser'];

        if (refreshedUser && refreshedUser.token) {
          console.log('Token refresh successful');

          // Restart monitoring with new token
          this.startTokenMonitoring(refreshedUser);
          return true;
        }

        console.warn('Token refresh did not return updated user');
        return false;
      } catch (error) {
        console.error('Token refresh failed:', error);

        // If refresh fails, the user might need to log in again
        // The auth store should have already cleared the user
        return false;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return await this.refreshPromise;
  }

  /**
   * Ensure token is valid before making API calls
   * @returns {Promise<boolean>} True if token is valid or was successfully refreshed
   */
  async ensureValidToken() {
    // If a refresh is already running, wait for it.
    if (this.isRefreshing && this.refreshPromise) {
      return await this.refreshPromise;
    }
    if (this.needsRefresh()) {
      return await this.refreshToken();
    }
    return true;
  }

  /**
   * Handle API response errors that might indicate expired tokens
   * @param {Response} response - Fetch response object
   * @returns {Promise<boolean>} True if error was handled and token refreshed
   */
  async handleApiError(response) {
    if (response.status === 401) {
      console.log('Received 401 response, attempting token refresh...');
      return await this.refreshToken();
    }
    return false;
  }
}

// Create singleton instance
const tokenManager = new TokenManager();

export default tokenManager;